import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import type { AppNotification, NotificationType } from '../types';
import { apiOrigin, tokenStore } from './api';

/** Nome de cada evento nas preferências e no painel (Fase 6 / N2). */
export const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  lead_assigned: 'Lead atribuído a você',
  business_assigned: 'Negócio atribuído a você',
  activity_assigned: 'Atividade atribuída a você',
  activity_due: 'Atividade vencendo (15 min antes)',
  message_received: 'Mensagem nova numa conversa sua',
  conversation_transferred: 'Conversa transferida para você',
  automation_error: 'Automação com erro (administradores)',
  job_finished: 'Importação ou exportação terminou',
  automation_notice: 'Aviso enviado por uma automação',
  sales_interview_due: 'Hora de lembrar o cliente da entrevista médica (Vendas)',
  distribution_sla: 'Lead passou do prazo de 1º contato (distribuição)',
};

/**
 * Notificações em tempo real: ouve a sala da pessoa no socket `/realtime`,
 * atualiza o contador e o painel, e devolve os pop-ups ainda na tela.
 * Se o socket cair, o contador ainda se atualiza pela consulta periódica.
 */
export function useNotificationsRealtime() {
  const queryClient = useQueryClient();
  const [toasts, setToasts] = useState<AppNotification[]>([]);

  useEffect(() => {
    const origin = apiOrigin();
    const socket = io(`${origin}/realtime`, {
      auth: (callback) => callback({ token: tokenStore.access ?? '' }),
      transports: ['websocket'],
    });
    socket.on('notification', (payload: { popup: boolean; notification: AppNotification }) => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      if (!payload?.popup || !payload.notification) return;
      const notice = payload.notification;
      setToasts((current) =>
        [notice, ...current.filter((item) => item.id !== notice.id)].slice(0, 3),
      );
      window.setTimeout(
        () => setToasts((current) => current.filter((item) => item.id !== notice.id)),
        7000,
      );
    });
    return () => {
      socket.disconnect();
    };
  }, [queryClient]);

  const dismiss = (id: string) =>
    setToasts((current) => current.filter((item) => item.id !== id));
  return { toasts, dismiss };
}
